/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IBreadcrumbViewProps } from '@msdyn365-commerce-modules/breadcrumb';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { _getScreenSize } from '../utilities/display-utils';
import { IBreadcrumbProps as IBreadcrumbExtensionProps } from '../definition-extensions/breadcrumb.ext.props.autogenerated';
import MsDyn365 from '@msdyn365-commerce/core';

const BreadCrumbView: React.FC<IBreadcrumbViewProps & IBreadcrumbExtensionProps<{}>> = props => {
    const {
        root,
        links,
        backButton,
        BreadcrumbContainer,
        BreadcrumbList,
        BreadcrumbListItem,
        separator,
        data: {
            categoryPaths: { result }
        },
        context,
        config
    } = props;
    const [screenSize, setScreenSize] = useState(_getScreenSize(context));
    if (!root && !links && !backButton) {
        props.context.telemetry.error('BreadCrumb content is empty, module wont render');
        return null;
    }

    const pageUrl = props.context.request.url.requestUrl.href.toString();

    useEffect(() => {
        if (!MsDyn365.isBrowser) {
            return;
        }

        setScreenSize(_getScreenSize(props.context));

        const handleResize = () => {
            const newScreenSize = _getScreenSize(props.context);
            setScreenSize(newScreenSize);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [props.context]);
    const isPDP = config.isPdpPage;
    if (isPDP) {
        const [expanded, setExpanded] = useState<boolean>(false);
        const isMobile = screenSize === 1;

        let shouldShowBackLink = null;
        if (MsDyn365.isBrowser) {
            shouldShowBackLink = /\/\d+\.c(\/)?$/.test(document.referrer) || document.referrer.includes('search?q');
        }
        const categoryNames = props.data.categoryPaths.result?.map(item => item.Name) || [];
        const totalLength = categoryNames.join('').length;
        const validCharacterLength = totalLength <= 43;

        const handleExpandClick = (e: React.MouseEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setExpanded(true);
        };

        const renderMobileLinks = () => {
            if (!links) return null;
            //TODO Item: Styling with dots
            if (!expanded && !validCharacterLength) {
                return (
                    <>
                        <Node {...BreadcrumbListItem} onClick={handleExpandClick}>
                            {links[0]}
                        </Node>
                        <Node {...BreadcrumbListItem}>…</Node>
                        <Node {...BreadcrumbListItem} className='breadcrumb_item-current'>
                            {links[links.length - 2]}
                        </Node>
                    </>
                );
            }

            return links.slice(0, -1).map((link, index) => (
                <Node
                    key={index}
                    {...BreadcrumbListItem}
                    className={classnames(BreadcrumbListItem.className, index === links.length - 2 ? 'breadcrumb_item-current' : '')}
                >
                    {link}
                    {index < links.length - 2 && separator}
                </Node>
            ));
        };

        const renderDesktopLinks = () => {
            if (!links || isMobile) return null;

            return links.slice(0, -1).map((link, index) => (
                <Node
                    key={index}
                    {...BreadcrumbListItem}
                    className={classnames(BreadcrumbListItem.className, index === links.length - 2 ? 'breadcrumb_item-current' : '')}
                >
                    {link}
                    {index < links.length - 2 && separator}
                </Node>
            ));
        };

        return (
            <Module
                {...BreadcrumbContainer}
                className={`ms-breadcrumb ${!expanded && !validCharacterLength && isMobile ? 'breadcrumb-collapsed' : ''}`}
            >
                {shouldShowBackLink && (
                    <button onClick={() => history.back()} className='breadcrumb-back-button'>
                        Back to results
                    </button>
                )}
                <Node {...BreadcrumbList}>
                    {root && (
                        <Node {...BreadcrumbListItem}>
                            {root} {links && separator}
                        </Node>
                    )}
                    {backButton && <Node {...BreadcrumbListItem}>{backButton}</Node>}

                    {isMobile ? renderMobileLinks() : renderDesktopLinks()}
                </Node>

                <Node className='' vocab='https://schema.org/' typeof='BreadcrumbList'>
                    {result && result.length > 0
                        ? result.map((item, index) => {
                              return (
                                  <Node tag='span' key={index} property='itemListElement' typeof='ListItem' className=''>
                                      <meta property='position' content={(index + 1)?.toString()} />
                                      {item.hasOwnProperty('Name') && item.Name !== undefined && (
                                          <meta
                                              property='Name'
                                              content={item.Name.split('-')
                                                  .join(' ')
                                                  .toString()}
                                          />
                                      )}
                                      {item.hasOwnProperty('Url') && item.Url !== undefined && (
                                          <meta property='item' content={pageUrl + item.Url.toString()} />
                                      )}
                                  </Node>
                              );
                          })
                        : null}
                </Node>
            </Module>
        );
    }

    return (
        <Module {...BreadcrumbContainer}>
            <Node {...BreadcrumbList}>
                {root && (
                    <Node {...BreadcrumbListItem}>
                        {root}
                        {links && separator}
                    </Node>
                )}
                {backButton && <Node {...BreadcrumbListItem}>{backButton}</Node>}
                {links &&
                    links.map((link, index) => {
                        return (
                            <React.Fragment key={index}>
                                <Node
                                    {...BreadcrumbListItem}
                                    className={classnames(
                                        BreadcrumbListItem.className,
                                        index === links.length - 1 ? 'breadcrumb_item-current' : ''
                                    )}
                                >
                                    {link}
                                    {links[index + 1] && separator}
                                </Node>
                            </React.Fragment>
                        );
                    })}
            </Node>

            {/** Node added for schema */}
            <Node className='' vocab='https://schema.org/' typeof='BreadcrumbList'>
                {result && result.length > 0
                    ? result.map((item, index) => {
                          return (
                              <Node tag='span' key={index} property='itemListElement' typeof='ListItem' className=''>
                                  <meta property='position' content={(index + 1)?.toString()} />
                                  {item.hasOwnProperty('Name') && item.Name !== undefined && (
                                      <meta
                                          property='Name'
                                          content={item.Name.split('-')
                                              .join(' ')
                                              .toString()}
                                      />
                                  )}
                                  {item.hasOwnProperty('Url') && item.Url !== undefined && (
                                      <meta property='item' content={pageUrl + item.Url.toString()} />
                                  )}
                              </Node>
                          );
                      })
                    : null}
            </Node>
        </Module>
    );
};

export default BreadCrumbView;
